import React, {useCallback, useEffect, useState} from 'react';
import Box from "@mui/joy/Box";
import Typography from "@mui/joy/Typography";
import Button from "@mui/joy/Button";
import Tabs from "@mui/joy/Tabs";
import TabList from "@mui/joy/TabList";
import Tab, {tabClasses} from "@mui/joy/Tab";
import TabPanel from "@mui/joy/TabPanel";
import FormControl from "@mui/joy/FormControl";
import FormLabel from "@mui/joy/FormLabel";
import Select from "@mui/joy/Select";
import Option from "@mui/joy/Option";
import Chip from "@mui/joy/Chip";
import {PaymentMethodsTable} from "./PaymentMethodsTable";
import {PaymentMethodModel, PaymentSystemType, PaymentOperationType} from "../../../models/paymentMethod";
import DebounceInput from "../../../components/debounceInput/DebounceInput";
import {useNavigate} from "react-router-dom";
import AddIcon from '@mui/icons-material/Add';
import {PaymentMethodsApi} from "./paymentMethodsApi";

interface PaymentMethodsTabProps {
    currentTabIndex?: number;
    onTabChange?: (index: number) => void;
}

export function PaymentMethodsTab({ currentTabIndex = 0, onTabChange }: PaymentMethodsTabProps) {
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState(currentTabIndex);

    React.useEffect(() => {
        setActiveTab(currentTabIndex);
    }, [currentTabIndex]);
    const [systemType, setSystemType] = useState<PaymentSystemType | undefined>(undefined);
    const [selectedCurrencies, setSelectedCurrencies] = useState<string[]>([]);
    const [searchTitle, setSearchTitle] = useState('');
    const [availableCurrencies, setAvailableCurrencies] = useState<string[]>([]);
    const [allPaymentMethods, setAllPaymentMethods] = useState<PaymentMethodModel[]>([]);

    useEffect(() => {
        const loadData = async () => {
            try {
                const allMethods = await PaymentMethodsApi.getPaymentMethods();
                setAllPaymentMethods(allMethods);
                const currencies = Array.from(new Set(allMethods.flatMap(method => method.currencies ?? [])));
                setAvailableCurrencies(currencies.sort());
            } catch (error) {
                console.error('Error loading payment methods:', error);
            }
        };
        loadData();
    }, []);

    const handleCurrencyChange = useCallback((event: React.SyntheticEvent | null, newValue: string | null) => {
        if (newValue && !selectedCurrencies.includes(newValue)) {
            setSelectedCurrencies([...selectedCurrencies, newValue]);
        }
    }, [selectedCurrencies]);

    const removeCurrency = useCallback((currencyToRemove: string) => {
        setSelectedCurrencies(prev => prev.filter(currency => currency !== currencyToRemove));
    }, []);

    const clearFilters = useCallback(() => {
        setSystemType(undefined);
        setSelectedCurrencies([]);
        setSearchTitle('');
    }, []);

    const hasActiveFilters = systemType !== undefined || selectedCurrencies.length > 0 || searchTitle !== '';

    return (
        <Box sx={{ width: '100%' }}>
            {/* Заголовок и кнопка */}
            <Box
                sx={{
                    display: 'flex',
                    mb: 1,
                    gap: 1,
                    flexDirection: { xs: 'column', sm: 'row' },
                    alignItems: { xs: 'start', sm: 'center' },
                    flexWrap: 'wrap',
                    justifyContent: 'space-between',
                }}
            >
                <Typography level="h3" component="h2">
                    Методы оплаты
                </Typography>
                <Button
                    startDecorator={<AddIcon/>}
                    onClick={() => navigate('/app/payment-methods/new')}
                    size="sm"
                >
                    Добавить новый метод
                </Button>
            </Box>

            {/* Фильтры */}
            <Box
                sx={{
                    display: 'flex',
                    gap: 2,
                    flexWrap: 'wrap',
                    alignItems: 'center',
                    p: 2,
                    bgcolor: 'background.level1',
                    borderRadius: '8px',
                    mb: 2
                }}
            >
                <FormControl size="sm" sx={{ minWidth: 120 }}>
                    <FormLabel>Тип системы</FormLabel>
                    <Select
                        value={systemType?.toString() ?? ''}
                        onChange={(event, newValue) => setSystemType(newValue ? Number(newValue) as PaymentSystemType : undefined)}
                        placeholder="Все типы"
                    >
                        <Option value="">Все типы</Option>
                        <Option value={PaymentSystemType.Tome.toString()}>Tome</Option>
                        <Option value={PaymentSystemType.GameMoney.toString()}>GameMoney</Option>
                    </Select>
                </FormControl>

                <FormControl size="sm" sx={{ minWidth: 150 }}>
                    <FormLabel>Валюта</FormLabel>
                    <Select
                        value=""
                        onChange={handleCurrencyChange}
                        placeholder="Выберите валюту"
                    >
                        {availableCurrencies.map(currency => (
                            <Option key={currency} value={currency}>
                                {currency}
                            </Option>
                        ))}
                    </Select>
                </FormControl>

                <FormControl size="sm" sx={{ minWidth: 200 }}>
                    <FormLabel>Поиск по названию</FormLabel>
                    <DebounceInput
                        placeholder="Введите название..."
                        value={searchTitle}
                        handleDebounce={(value) => setSearchTitle(value)}
                        debounceTimeout={300}
                    />
                </FormControl>

                {hasActiveFilters && (
                    <Chip
                        variant="soft"
                        color="neutral"
                        onClick={clearFilters}
                        sx={{ cursor: 'pointer' }}
                    >
                        Очистить фильтры
                    </Chip>
                )}
            </Box>

            {/* Выбранные валюты */}
            {selectedCurrencies.length > 0 && (
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
                    <Typography level="body-sm" sx={{ alignSelf: 'center' }}>
                        Выбранные валюты:
                    </Typography>
                    {selectedCurrencies.map(currency => (
                        <Chip
                            key={currency}
                            size="sm"
                            variant="soft"
                            color="primary"
                            onClick={() => removeCurrency(currency)}
                            sx={{ cursor: 'pointer' }}
                        >
                            {currency} ×
                        </Chip>
                    ))}
                </Box>
            )}

            <Tabs 
                value={activeTab} 
                onChange={(event, newValue) => {
                    const idx = (newValue as number) ?? 0;
                    setActiveTab(idx);
                    onTabChange?.(idx);
                }}
                sx={{bgcolor: 'transparent'}}
            >
                <TabList
                    tabFlex={1}
                    size="sm"
                    sx={{
                        pl: {xs: 0, md: 4},
                        justifyContent: 'left',
                        [`&& .${tabClasses.root}`]: {
                            fontWeight: '600',
                            flex: 'initial',
                            color: 'text.tertiary',
                            [`&.${tabClasses.selected}`]: {
                                bgcolor: 'transparent',
                                color: 'text.primary',
                                '&::after': {
                                    height: '2px',
                                    bgcolor: 'primary.500',
                                },
                            },
                        },
                    }}
                >
                    <Tab sx={{borderRadius: '6px 6px 0 0'}} indicatorInset value={0}>
                        Пополнение
                    </Tab>
                    <Tab sx={{borderRadius: '6px 6px 0 0'}} indicatorInset value={1}>
                        Вывод
                    </Tab>
                </TabList>
                
                <TabPanel value={0}>
                    <PaymentMethodsTable
                        operationType={PaymentOperationType.Deposit}
                        systemType={systemType}
                        currencies={selectedCurrencies.length > 0 ? selectedCurrencies : undefined}
                        searchTitle={searchTitle || undefined}
                        allPaymentMethods={allPaymentMethods}
                        onDeleted={(id) => setAllPaymentMethods(allPaymentMethods.filter(m => m.id !== id))}
                    />
                </TabPanel>
                
                <TabPanel value={1}>
                    <PaymentMethodsTable
                        operationType={PaymentOperationType.Withdraw}
                        systemType={systemType}
                        currencies={selectedCurrencies.length > 0 ? selectedCurrencies : undefined}
                        searchTitle={searchTitle || undefined}
                        allPaymentMethods={allPaymentMethods}
                        onDeleted={(id) => setAllPaymentMethods(allPaymentMethods.filter(m => m.id !== id))}
                    />
                </TabPanel>
            </Tabs>
        </Box>
    );
}
